import React, { useEffect, useState } from 'react';
import {
    Button,
    Col,
    CustomInput,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Row,
} from 'reactstrap';
import Swal from 'sweetalert2';
import xlsx from 'node-xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCloudUploadAlt,
    faFileExcel,
    faIdCard,
    faSave,
    faSpinner,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { listPaises, updateSujeto, uploadDocumento } from '../api/pldApi';
import { alertarSiPaisGafi } from '../utils/paisesGafi';
import Requester from '../../shared/Requester';
import HomonimiaModal from './HomonimiaModal';
import FormDomicilio from './FormDomicilio';

const AUTO_AUTORIDAD = {
    INE: 'INE',
    PASAPORTE: 'Secretaría de Relaciones Exteriores',
    CEDULA: 'Dirección General de Profesiones (SEP)',
    INM: 'Instituto Nacional de Migración (INM)',
};

function satDateToInput(v) {
    const s = String(v || '').replace(/\D/g, '');
    if (s.length === 8) {
        return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    }
    if (v && String(v).length >= 10) {
        return String(v).slice(0, 10);
    }
    return '';
}

function inputDateToSat(v) {
    return String(v || '').replace(/-/g, '');
}

function nombreCompletoDesdeSujeto(s) {
    if (!s) return '';
    return [s.nombre, s.paterno, s.materno]
        .filter((x) => x && String(x).trim())
        .join(' ')
        .trim();
}

const REP_LEGAL_KEYS = [
    'nombre',
    'paterno',
    'materno',
    'rfc',
    'curp',
    'pld_fecha_nac_const',
    'pld_pais_nacimiento',
    'pld_lugar_nacimiento',
    'pld_lugar_residencia',
    'pld_nacionalidad',
    'pld_condicion_estancia',
    'pld_ocupacion',
    'pld_telefono',
    'pld_correo',
    'pld_id_tipo',
    'pld_id_numero',
    'pld_id_autoridad',
    'pld_calle',
    'pld_num_ext',
    'pld_num_int',
    'pld_colonia',
    'pld_cp',
    'pld_ciudad',
    'pld_estado',
    'pld_pais_domicilio',
];

/** Encabezados del export Handkey (docs/Identificación Handkey (respuestas).xlsx), normalizados. */
const HANDKEY_HEADERS = {
    MARCA_TEMPORAL: 'marca temporal',
    NOMBRE: 'nombre(s)',
    PATERNO: 'apellido paterno',
    MATERNO: 'apellido materno',
    RFC: 'rfc',
    FECHA_NAC: 'fecha de nacimiento',
    PAIS_NACIONALIDAD: 'pais nacionalidad',
    PAIS_NACIMIENTO: 'pais de nacimiento',
    ACTIVIDAD: 'actividad/giro',
    LUGAR_NACIMIENTO: 'lugar de nacimiento',
    LUGAR_RESIDENCIA: 'lugar de recidencia',
    CONDICION_ESTANCIA: 'condicion de estancia',
    CURP: 'curp',
    TELEFONO: 'telefono de contacto',
    CORREO: 'correo electronico',
    CALLE: 'domicilio - calle',
    NUM_EXT: 'numero exterior',
    NUM_INT: 'numero interior',
    COLONIA: 'colonia',
    CP: 'cp - codigo postal',
    CIUDAD: 'ciudad, alcaldia o municipio',
    ESTADO: 'estado',
    PAIS_DOMICILIO: 'pais',
    ID_TIPO: 'tipo de identificacion',
    ID_NUMERO: 'numero de identificacion',
};

const HANDKEY_REQUIRED_HEADERS = [
    HANDKEY_HEADERS.NOMBRE,
    HANDKEY_HEADERS.PATERNO,
    HANDKEY_HEADERS.MATERNO,
    HANDKEY_HEADERS.RFC,
    HANDKEY_HEADERS.FECHA_NAC,
    HANDKEY_HEADERS.PAIS_NACIONALIDAD,
    HANDKEY_HEADERS.PAIS_NACIMIENTO,
    HANDKEY_HEADERS.ACTIVIDAD,
    HANDKEY_HEADERS.LUGAR_NACIMIENTO,
    HANDKEY_HEADERS.LUGAR_RESIDENCIA,
    HANDKEY_HEADERS.CONDICION_ESTANCIA,
    HANDKEY_HEADERS.CURP,
    HANDKEY_HEADERS.TELEFONO,
    HANDKEY_HEADERS.CORREO,
    HANDKEY_HEADERS.CALLE,
    HANDKEY_HEADERS.NUM_EXT,
    HANDKEY_HEADERS.NUM_INT,
    HANDKEY_HEADERS.COLONIA,
    HANDKEY_HEADERS.CP,
    HANDKEY_HEADERS.CIUDAD,
    HANDKEY_HEADERS.ESTADO,
    HANDKEY_HEADERS.PAIS_DOMICILIO,
    HANDKEY_HEADERS.ID_TIPO,
    HANDKEY_HEADERS.ID_NUMERO,
];

const ID_TIPO_HANDKEY = {
    'INE / IFE': 'INE',
    INE: 'INE',
    IFE: 'INE',
    PASAPORTE: 'PASAPORTE',
    'CÉDULA PROFESIONAL': 'CEDULA',
    'CEDULA PROFESIONAL': 'CEDULA',
    CEDULA: 'CEDULA',
    'DOC. MIGRATORIO': 'INM',
    'DOCUMENTO MIGRATORIO': 'INM',
    INM: 'INM',
};

function normalizeHeader(h) {
    return String(h || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeIdentidad(s) {
    return String(s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeRfc(s) {
    return normalizeIdentidad(s).replace(/[^A-Z0-9Ñ&]/g, '');
}

function cellToString(v) {
    if (v == null || v === '') return '';
    if (typeof v === 'number') {
        if (Number.isFinite(v) && Math.abs(v - Math.round(v)) < 1e-9) {
            return String(Math.round(v));
        }
        return String(v);
    }
    return String(v).trim();
}

/** Excel serial date → YYYY-MM-DD (epoch 1899-12-30). */
function excelSerialToInputDate(serial) {
    const n = Number(serial);
    if (!Number.isFinite(n) || n <= 0) return '';
    const whole = Math.floor(n);
    const ms = Date.UTC(1899, 11, 30) + whole * 86400000;
    const d = new Date(ms);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function parseHandkeyFecha(raw) {
    if (raw == null || raw === '') return '';
    if (typeof raw === 'number') return excelSerialToInputDate(raw);
    const s = String(raw).trim();
    if (/^\d+(\.\d+)?$/.test(s)) return excelSerialToInputDate(Number(s));
    return satDateToInput(s);
}

function resolvePaisClave(raw, paises) {
    const text = cellToString(raw);
    if (!text) return '';
    const upper = text.toUpperCase();
    if (/^[A-Z]{3}$/.test(upper)) return upper;
    const norm = normalizeIdentidad(text);
    const list = Array.isArray(paises) ? paises : [];
    const byNombre = list.find((p) => normalizeIdentidad(p.nombre) === norm);
    if (byNombre) return byNombre.clave;
    const byClave = list.find((p) => normalizeIdentidad(p.clave) === norm);
    if (byClave) return byClave.clave;
    if (norm === 'MEXICO' || norm === 'MEX') return 'MEX';
    return text;
}

function mapTipoIdentificacion(raw) {
    const text = cellToString(raw);
    if (!text) return '';
    const direct = ID_TIPO_HANDKEY[text] || ID_TIPO_HANDKEY[normalizeIdentidad(text)];
    if (direct) return direct;
    const norm = normalizeIdentidad(text);
    if (norm.includes('INE') || norm.includes('IFE')) return 'INE';
    if (norm.includes('PASAPORTE')) return 'PASAPORTE';
    if (norm.includes('CEDULA')) return 'CEDULA';
    if (norm.includes('MIGRA') || norm.includes('INM')) return 'INM';
    return text;
}

function rowToHandkeyMap(headers, row) {
    const map = {};
    for (let i = 0; i < headers.length; i += 1) {
        const key = normalizeHeader(headers[i]);
        if (!key || key === HANDKEY_HEADERS.MARCA_TEMPORAL) continue;
        map[key] = row[i];
    }
    return map;
}

function parseHandkeyXls(buffer) {
    const sheets = xlsx.parse(buffer, { type: 'array' });
    if (!sheets || sheets.length === 0 || !sheets[0].data || sheets[0].data.length < 2) {
        throw new Error('El archivo no tiene respuestas. Se espera encabezado + al menos una fila.');
    }
    const data = sheets[0].data;
    const headers = (data[0] || []).map((h) => String(h || ''));
    const headerNorms = headers.map(normalizeHeader);
    const missing = HANDKEY_REQUIRED_HEADERS.filter((req) => {
        const reqNorm = normalizeHeader(req);
        return !headerNorms.some((h) => h === reqNorm || h.replace(/,$/, '') === reqNorm);
    });
    if (missing.length > 0) {
        throw new Error(
            `Formato inválido. Faltan columnas del formulario Handkey: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`
        );
    }
    const rows = [];
    for (let i = 1; i < data.length; i += 1) {
        const row = data[i] || [];
        if (!row.some((c) => c != null && String(c).trim() !== '')) continue;
        rows.push(rowToHandkeyMap(headers, row));
    }
    if (rows.length === 0) {
        throw new Error('El archivo no contiene filas de respuesta.');
    }
    return rows;
}

function findHandkeyRowForSujeto(rows, sujeto) {
    const nombre = normalizeIdentidad(sujeto.nombre);
    const paterno = normalizeIdentidad(sujeto.paterno);
    const materno = normalizeIdentidad(sujeto.materno);
    const rfc = normalizeRfc(sujeto.rfc);
    return (
        rows.find((r) => {
            return (
                normalizeIdentidad(r[HANDKEY_HEADERS.NOMBRE]) === nombre &&
                normalizeIdentidad(r[HANDKEY_HEADERS.PATERNO]) === paterno &&
                normalizeIdentidad(r[HANDKEY_HEADERS.MATERNO]) === materno &&
                normalizeRfc(r[HANDKEY_HEADERS.RFC]) === rfc
            );
        }) || null
    );
}

function applyHandkeyRowToFormData(prev, row, paises) {
    const next = {
        ...prev,
        representante_legal: { ...prev.representante_legal },
    };
    const idTipo = mapTipoIdentificacion(row[HANDKEY_HEADERS.ID_TIPO]);
    next.pld_fecha_nac_const = parseHandkeyFecha(row[HANDKEY_HEADERS.FECHA_NAC]);
    next.pld_pais_nacionalidad =
        resolvePaisClave(row[HANDKEY_HEADERS.PAIS_NACIONALIDAD], paises) || next.pld_pais_nacionalidad;
    next.pld_pais_nacimiento =
        resolvePaisClave(row[HANDKEY_HEADERS.PAIS_NACIMIENTO], paises) || next.pld_pais_nacimiento;
    next.pld_actividad_giro_descripcion = cellToString(row[HANDKEY_HEADERS.ACTIVIDAD]);
    next.pld_lugar_nacimiento = cellToString(row[HANDKEY_HEADERS.LUGAR_NACIMIENTO]);
    next.pld_lugar_residencia = cellToString(row[HANDKEY_HEADERS.LUGAR_RESIDENCIA]);
    next.pld_condicion_estancia = cellToString(row[HANDKEY_HEADERS.CONDICION_ESTANCIA]);
    next.rfc = cellToString(row[HANDKEY_HEADERS.RFC]).toUpperCase();
    next.curp = cellToString(row[HANDKEY_HEADERS.CURP]).toUpperCase();
    next.pld_telefono = cellToString(row[HANDKEY_HEADERS.TELEFONO]);
    next.pld_correo = cellToString(row[HANDKEY_HEADERS.CORREO]);
    next.pld_calle = cellToString(row[HANDKEY_HEADERS.CALLE]);
    next.pld_num_ext = cellToString(row[HANDKEY_HEADERS.NUM_EXT]);
    next.pld_num_int = cellToString(row[HANDKEY_HEADERS.NUM_INT]);
    next.pld_colonia = cellToString(row[HANDKEY_HEADERS.COLONIA]);
    next.pld_cp = cellToString(row[HANDKEY_HEADERS.CP]);
    next.pld_ciudad = cellToString(row[HANDKEY_HEADERS.CIUDAD]);
    next.pld_estado = cellToString(row[HANDKEY_HEADERS.ESTADO]);
    next.pld_pais_domicilio =
        resolvePaisClave(row[HANDKEY_HEADERS.PAIS_DOMICILIO], paises) || next.pld_pais_domicilio;
    if (idTipo) {
        next.pld_id_tipo = idTipo;
        next.pld_id_autoridad = AUTO_AUTORIDAD[idTipo] || next.pld_id_autoridad;
    }
    next.pld_id_numero = cellToString(row[HANDKEY_HEADERS.ID_NUMERO]);
    return next;
}

/** Solo campos dirty y no vacíos; evita sobrescribir datos existentes con blancos. */
function buildRepresentanteLegalDirty(actual, inicial) {
    const dirty = {};
    const act = actual || {};
    const ini = inicial || {};
    for (let i = 0; i < REP_LEGAL_KEYS.length; i += 1) {
        const key = REP_LEGAL_KEYS[i];
        let next = act[key];
        let prev = ini[key];
        if (key === 'pld_fecha_nac_const') {
            next = inputDateToSat(next);
            prev = inputDateToSat(prev);
        }
        const nextS = String(next || '').trim();
        const prevS = String(prev || '').trim();
        if (nextS === prevS) continue;
        if (!nextS) continue;
        dirty[key] =
            key === 'rfc' || key === 'curp' || key === 'pld_id_autoridad'
                ? nextS.toUpperCase()
                : nextS;
    }
    if (Object.keys(dirty).length === 0) return null;
    if (!dirty.rfc) {
        const rfc = String(act.rfc || ini.rfc || '')
            .trim()
            .toUpperCase();
        if (!rfc) return null;
        dirty.rfc = rfc;
    }
    return dirty;
}

export default function ModalIdentificacion({
    unidadId,
    sujeto,
    onClose,
    onSave,
    readOnly = false,
}) {
    const [tab, setTab] = useState('formulario');
    const [guardando, setGuardando] = useState(false);
    const [subiendoArchivo, setSubiendoArchivo] = useState(false);
    const [cargandoXls, setCargandoXls] = useState(false);
    const [paises, setPaises] = useState([]);
    const [homonimia, setHomonimia] = useState({
        open: false,
        resumen: null,
        nombreCompleto: '',
    });

    const esMoral =
        sujeto.tipo_persona === 'Moral' || sujeto.tipo_persona === 'Fideicomiso';

    const [formData, setFormData] = useState({
        pld_fecha_nac_const: satDateToInput(sujeto.pld_fecha_nac_const),
        pld_pais_nacionalidad: sujeto.pld_pais_nacionalidad || 'MEX',
        pld_pais_nacimiento: sujeto.pld_pais_nacimiento || 'MEX',
        pld_lugar_nacimiento: sujeto.pld_lugar_nacimiento || '',
        pld_lugar_residencia: sujeto.pld_lugar_residencia || '',
        pld_condicion_estancia: sujeto.pld_condicion_estancia || '',
        pld_ocupacion: sujeto.pld_ocupacion || '',
        pld_actividad_giro_descripcion: sujeto.pld_actividad_giro_descripcion || '',
        rfc: sujeto.rfc || '',
        curp: sujeto.curp || '',
        pld_calle: sujeto.pld_calle || '',
        pld_num_ext: sujeto.pld_num_ext || '',
        pld_num_int: sujeto.pld_num_int || '',
        pld_colonia: sujeto.pld_colonia || '',
        pld_cp: sujeto.pld_cp || '',
        pld_ciudad: sujeto.pld_ciudad || '',
        pld_estado: sujeto.pld_estado || '',
        pld_pais_domicilio: sujeto.pld_pais_domicilio || 'MEX',
        pld_telefono: sujeto.pld_telefono || '',
        pld_correo: sujeto.pld_correo || '',
        pld_id_tipo: sujeto.pld_id_tipo || 'INE',
        pld_id_numero: sujeto.pld_id_numero || '',
        pld_id_autoridad: sujeto.pld_id_autoridad || 'INE',
        pld_conoce_beneficiario_controlador: sujeto.pld_conoce_beneficiario_controlador || '',
        pld_beneficiario_controlador_nombre: sujeto.pld_beneficiario_controlador_nombre || '',
        representante_legal: {
            nombre: (sujeto.representante_legal && sujeto.representante_legal.nombre) || '',
            paterno: (sujeto.representante_legal && sujeto.representante_legal.paterno) || '',
            materno: (sujeto.representante_legal && sujeto.representante_legal.materno) || '',
            rfc: (sujeto.representante_legal && sujeto.representante_legal.rfc) || '',
            curp: (sujeto.representante_legal && sujeto.representante_legal.curp) || '',
            pld_fecha_nac_const: satDateToInput(
                sujeto.representante_legal && sujeto.representante_legal.pld_fecha_nac_const
            ),
            pld_pais_nacimiento:
                (sujeto.representante_legal && sujeto.representante_legal.pld_pais_nacimiento) || 'MEX',
            pld_lugar_nacimiento:
                (sujeto.representante_legal && sujeto.representante_legal.pld_lugar_nacimiento) || '',
            pld_lugar_residencia:
                (sujeto.representante_legal && sujeto.representante_legal.pld_lugar_residencia) || '',
            pld_nacionalidad:
                (sujeto.representante_legal && sujeto.representante_legal.pld_nacionalidad) || '',
            pld_condicion_estancia:
                (sujeto.representante_legal && sujeto.representante_legal.pld_condicion_estancia) || '',
            pld_ocupacion:
                (sujeto.representante_legal && sujeto.representante_legal.pld_ocupacion) || '',
            pld_telefono:
                (sujeto.representante_legal && sujeto.representante_legal.pld_telefono) || '',
            pld_correo:
                (sujeto.representante_legal && sujeto.representante_legal.pld_correo) || '',
            pld_id_tipo:
                (sujeto.representante_legal && sujeto.representante_legal.pld_id_tipo) || 'INE',
            pld_id_numero:
                (sujeto.representante_legal && sujeto.representante_legal.pld_id_numero) || '',
            pld_id_autoridad:
                (sujeto.representante_legal && sujeto.representante_legal.pld_id_autoridad) ||
                'INE',
            pld_calle:
                (sujeto.representante_legal && sujeto.representante_legal.pld_calle) || '',
            pld_num_ext:
                (sujeto.representante_legal && sujeto.representante_legal.pld_num_ext) || '',
            pld_num_int:
                (sujeto.representante_legal && sujeto.representante_legal.pld_num_int) || '',
            pld_colonia:
                (sujeto.representante_legal && sujeto.representante_legal.pld_colonia) || '',
            pld_cp:
                (sujeto.representante_legal && sujeto.representante_legal.pld_cp) || '',
            pld_ciudad:
                (sujeto.representante_legal && sujeto.representante_legal.pld_ciudad) || '',
            pld_estado:
                (sujeto.representante_legal && sujeto.representante_legal.pld_estado) || '',
            pld_pais_domicilio:
                (sujeto.representante_legal && sujeto.representante_legal.pld_pais_domicilio) || 'MEX',
        },
    });

    const [repLegalInicial] = useState(() => ({ ...formData.representante_legal }));

    useEffect(() => {
        if (!unidadId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const data = await listPaises(unidadId);
                if (
                    !cancelled &&
                    Array.isArray(data) &&
                    data.length > 0
                ) {
                    setPaises(
                        data.map((p) => ({
                            clave: p.clave || p.id,
                            nombre: p.nombre || p.clave,
                        }))
                    );
                }
            } catch (_) {
                if (!cancelled) setPaises([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [unidadId]);

    useEffect(() => {
        if (esMoral && tab === 'xls') {
            setTab('formulario');
        }
    }, [esMoral, tab]);

    async function consultarHomonimiaTrasGuardar() {
        const nombreCompleto = nombreCompletoDesdeSujeto(sujeto);
        if (!nombreCompleto) return;
        try {
            const res = await Requester.get('pld/resumen', {
                params: { nombreCompleto },
            });
            const data = res && res.data;
            if (data && data.existe === true) {
                setHomonimia({
                    open: true,
                    resumen: data,
                    nombreCompleto,
                });
            }
        } catch (_) {
            /* resumen opcional; no bloquear cierre del modal principal */
        }
    }

    async function handleSave() {
        if (readOnly) return;
        setGuardando(true);
        const fechaLimpia = inputDateToSat(formData.pld_fecha_nac_const);
        const { representante_legal: _repOmit, ...restForm } = formData;
        const payload = {
            ...restForm,
            pld_fecha_nac_const: fechaLimpia,
        };
        if (esMoral) {
            const repDirty = buildRepresentanteLegalDirty(
                formData.representante_legal,
                repLegalInicial
            );
            if (repDirty) {
                payload.representante_legal = repDirty;
            }
        }
        try {
            const updated = await updateSujeto(unidadId, sujeto.id, payload);
            if (typeof onSave === 'function') {
                onSave(updated || payload);
            }
            onClose();
            await consultarHomonimiaTrasGuardar();
        } catch (error) {
            const mensaje =
                (error &&
                    error.response &&
                    error.response.data &&
                    error.response.data.error) ||
                error.message ||
                'Error desconocido';
            await Swal.fire({
                icon: 'error',
                title: 'Error al guardar',
                text: String(mensaje),
            });
        } finally {
            setGuardando(false);
        }
    }

    async function handleSubirFormato(e) {
        if (readOnly) return;
        const file = e.target.files && e.target.files[0];
        if (!file || !unidadId) return;
        setSubiendoArchivo(true);
        try {
            const res = await uploadDocumento(
                unidadId,
                sujeto.id,
                'formato_id_fisico',
                file
            );
            if (res && res.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Formato integrado',
                    text: 'El archivo físico quedó vinculado al expediente.',
                });
                if (typeof onSave === 'function') {
                    onSave({ documentos_status: res.nuevoStatus });
                }
                onClose();
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error al subir',
                    text: (res && res.error) || 'Respuesta inválida',
                });
            }
        } catch (error) {
            const mensaje =
                (error &&
                    error.response &&
                    error.response.data &&
                    error.response.data.error) ||
                error.message ||
                'Error al subir';
            await Swal.fire({
                icon: 'error',
                title: 'Error al subir',
                text: String(mensaje),
            });
        } finally {
            setSubiendoArchivo(false);
            e.target.value = '';
        }
    }

    async function handleSubirXls(e) {
        if (readOnly || esMoral) return;
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setCargandoXls(true);
        try {
            const buffer = await file.arrayBuffer();
            const rows = parseHandkeyXls(new Uint8Array(buffer));
            const match = findHandkeyRowForSujeto(rows, sujeto);
            if (!match) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Datos no coinciden',
                    text:
                        'Ninguna fila del Excel coincide con el nombre, apellidos y RFC del sujeto actual.',
                });
                return;
            }
            const next = applyHandkeyRowToFormData(formData, match, paises);
            setFormData(next);
            setTab('formulario');
            await Swal.fire({
                icon: 'success',
                title: 'Excel cargado',
                text: 'Se cargaron las respuestas Handkey en el formulario. Revise y pulse Guardar.',
            });
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error al leer Excel',
                text: String((error && error.message) || 'Archivo inválido'),
            });
        } finally {
            setCargandoXls(false);
            e.target.value = '';
        }
    }

    return (
        <>
            <Modal isOpen toggle={onClose} scrollable backdrop="static" size="xl">
                <ModalHeader className="bg-dark text-white">
                    <div className="small text-white-50 font-weight-normal mt-1">
                        Identificación PLD:
                    </div>
                    {sujeto.nombre.toUpperCase() || ''} {sujeto.paterno.toUpperCase() || ''} {sujeto.materno.toUpperCase() || ''}
                </ModalHeader>
                <ModalBody className="bg-light">
                    <Nav tabs className="mb-3">
                        <NavItem>
                            <NavLink
                                href="#"
                                className={tab === 'formulario' ? 'active' : ''}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setTab('formulario');
                                }}
                            >
                                1. Captura de datos manual
                            </NavLink>
                        </NavItem>

                        {!esMoral ? (
                            <NavItem>
                                <NavLink
                                    href="#"
                                    className={tab === 'xls' ? 'active' : ''}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setTab('xls');
                                    }}
                                >
                                    2. Subir información desde Archivo
                                </NavLink>
                            </NavItem>
                        ) : null}

                        <NavItem>
                            <NavLink
                                href="#"
                                className={tab === 'subir' ? 'active' : ''}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setTab('subir');
                                }}
                            >
                                {esMoral ? '2' : '3'}. Subir formato físico firmado
                            </NavLink>
                        </NavItem>
                    </Nav>

                    {tab === 'formulario' ? (
                        <fieldset disabled={readOnly}>
                            <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3">
                                <FontAwesomeIcon icon={faIdCard} className="mr-2" />
                                A. Identidad y origen
                            </h6>
                            <Row>
                                <Col md="3">
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted text-uppercase">
                                            Fecha {esMoral ? 'constitución' : 'nacimiento'}
                                        </Label>
                                        <Input
                                            type="date"
                                            value={formData.pld_fecha_nac_const}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    pld_fecha_nac_const: e.target.value,
                                                })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted text-uppercase">
                                            País nacionalidad
                                        </Label>
                                        <Input
                                            type="select"
                                            value={formData.pld_pais_nacionalidad}
                                            onChange={(e) => {
                                                const clave = e.target.value;
                                                const pais = paises.find((p) => p.clave === clave);
                                                setFormData({
                                                    ...formData,
                                                    pld_pais_nacionalidad: clave,
                                                });
                                                alertarSiPaisGafi(clave, pais && pais.nombre);
                                            }}
                                        >
                                            {paises.map((p) => (
                                                <option key={p.clave} value={p.clave}>
                                                    {p.nombre}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                {!esMoral ? (
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="small font-weight-bold text-muted text-uppercase">
                                                País nacimiento
                                            </Label>
                                            <Input
                                                type="select"
                                                value={formData.pld_pais_nacimiento}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        pld_pais_nacimiento: e.target.value,
                                                    })
                                                }
                                            >
                                                {paises.map((p) => (
                                                    <option key={p.clave} value={p.clave}>
                                                        {p.nombre}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                ) : null}
                                <Col md={esMoral ? 6 : 3}>
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted text-uppercase">
                                            Actividad / giro
                                        </Label>
                                        <Input
                                            value={formData.pld_actividad_giro_descripcion}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    pld_actividad_giro_descripcion: e.target.value,
                                                })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                                {!esMoral ? (
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="small font-weight-bold text-muted text-uppercase">
                                                Lugar de nacimiento
                                            </Label>
                                            <Input
                                                value={formData.pld_lugar_nacimiento}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        pld_lugar_nacimiento: e.target.value,
                                                    })
                                                }
                                            />
                                        </FormGroup>
                                    </Col>
                                ) : null}
                                {!esMoral ? (
                                <Col md="3">
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted text-uppercase">
                                            Lugar de residencia
                                        </Label>
                                        <Input
                                            value={formData.pld_lugar_residencia}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    pld_lugar_residencia: e.target.value,
                                                })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                                ) : null}
                                {!esMoral ? (
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="small font-weight-bold text-muted text-uppercase">
                                                Condición de estancia
                                            </Label>
                                            <Input
                                                value={formData.pld_condicion_estancia}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        pld_condicion_estancia: e.target.value,
                                                    })
                                                }
                                            />
                                        </FormGroup>
                                    </Col>
                                ) : null}
                            </Row>

                            <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3 mt-4">
                                B. Identificadores y contacto
                            </h6>
                            <Row>
                                <Col md="3">
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted text-uppercase">
                                            RFC
                                        </Label>
                                        <Input
                                            maxLength={esMoral ? 12 : 13}
                                            className="text-uppercase"
                                            value={formData.rfc}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    rfc: e.target.value.toUpperCase(),
                                                })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                                {!esMoral ? (
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="small font-weight-bold text-muted text-uppercase">
                                                CURP
                                            </Label>
                                            <Input
                                                maxLength={18}
                                                className="text-uppercase"
                                                value={formData.curp}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        curp: e.target.value.toUpperCase(),
                                                    })
                                                }
                                            />
                                        </FormGroup>
                                    </Col>
                                ) : null}
                                <Col md={esMoral ? 6 : 3}>
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted text-uppercase">
                                            Teléfono
                                        </Label>
                                        <Input
                                            value={formData.pld_telefono}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    pld_telefono: e.target.value,
                                                })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted text-uppercase">
                                            Correo
                                        </Label>
                                        <Input
                                            type="email"
                                            value={formData.pld_correo}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    pld_correo: e.target.value,
                                                })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FormDomicilio
                                titulo={esMoral ? 'C. Domicilio de la empresa' : 'C. Domicilio'}
                                data={formData}
                                paises={paises}
                                onChange={(field, value) =>
                                    setFormData({ ...formData, [field]: value })
                                }
                            />

                            {!esMoral ? (
                                <>
                                    <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3 mt-4">
                                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                                        D. Identificación oficial
                                    </h6>
                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Tipo de identificación
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={formData.pld_id_tipo}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            pld_id_tipo: e.target.value,
                                                            pld_id_autoridad: AUTO_AUTORIDAD[e.target.value] || '',
                                                        })
                                                    }
                                                >
                                                    <option value="INE">INE / IFE</option>
                                                    <option value="PASAPORTE">Pasaporte</option>
                                                    <option value="CEDULA">Cédula profesional</option>
                                                    <option value="INM">Doc. migratorio</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Autoridad emisora
                                                </Label>
                                                <Input
                                                    className="text-uppercase"
                                                    value={formData.pld_id_autoridad}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            pld_id_autoridad: e.target.value.toUpperCase(),
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Número de identificación
                                                </Label>
                                                <Input
                                                    value={formData.pld_id_numero}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            pld_id_numero: e.target.value,
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </>
                            ) : null}

                            {esMoral ? (
                                <div className="border border-primary rounded p-3 mt-4 bg-white">
                                    <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3">
                                        E. Representante legal
                                    </h6>
                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Nombre
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.nombre}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                nombre: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Apellido paterno
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.paterno}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                paterno: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Apellido materno
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.materno}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                materno: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Fecha nacimiento
                                                </Label>
                                                <Input
                                                    type="date"
                                                    value={
                                                        formData.representante_legal
                                                            .pld_fecha_nac_const
                                                    }
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_fecha_nac_const: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    RFC (13)
                                                </Label>
                                                <Input
                                                    maxLength={13}
                                                    className="text-uppercase"
                                                    value={formData.representante_legal.rfc}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                rfc: e.target.value.toUpperCase(),
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    CURP
                                                </Label>
                                                <Input
                                                    maxLength={18}
                                                    className="text-uppercase"
                                                    value={formData.representante_legal.curp}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                curp: e.target.value.toUpperCase(),
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    País de nacimiento
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={formData.representante_legal.pld_pais_nacimiento}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_pais_nacimiento: e.target.value,
                                                            },
                                                        })
                                                    }
                                                >
                                                    {paises.map((p) => (
                                                        <option key={p.clave} value={p.clave}>
                                                            {p.nombre}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Lugar de nacimiento
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.pld_lugar_nacimiento}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_lugar_nacimiento: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Lugar de residencia
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.pld_lugar_residencia}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_lugar_residencia: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Nacionalidad
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.pld_nacionalidad}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_nacionalidad: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Condición de estancia
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.pld_condicion_estancia}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_condicion_estancia: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Ocupación
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.pld_ocupacion}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_ocupacion: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Teléfono
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.pld_telefono}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_telefono: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Correo electrónico
                                                </Label>
                                                <Input
                                                    type="email"
                                                    value={formData.representante_legal.pld_correo}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_correo: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3 mt-4">
                                        F. Datos de la identificación
                                    </h6>
                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Tipo de identificación
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={formData.representante_legal.pld_id_tipo}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_id_tipo: e.target.value,
                                                                pld_id_autoridad: AUTO_AUTORIDAD[e.target.value] || '',
                                                            },
                                                        })
                                                    }
                                                >
                                                    <option value="INE">INE / IFE</option>
                                                    <option value="PASAPORTE">Pasaporte</option>
                                                    <option value="CEDULA">Cédula profesional</option>
                                                    <option value="INM">Doc. migratorio</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Autoridad emisora
                                                </Label>
                                                <Input
                                                    className="text-uppercase"
                                                    value={
                                                        formData.representante_legal.pld_id_autoridad
                                                    }
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_id_autoridad:
                                                                    e.target.value.toUpperCase(),
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label className="small font-weight-bold text-muted text-uppercase">
                                                    Número de identificación
                                                </Label>
                                                <Input
                                                    value={formData.representante_legal.pld_id_numero}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            representante_legal: {
                                                                ...formData.representante_legal,
                                                                pld_id_numero: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <FormDomicilio
                                        titulo="G. Domicilio del representante legal"
                                        data={formData.representante_legal}
                                        paises={paises}
                                        onChange={(field, value) =>
                                            setFormData({
                                                ...formData,
                                                representante_legal: {
                                                    ...formData.representante_legal,
                                                    [field]: value,
                                                },
                                            })
                                        }
                                    />
                                </div>
                            ) : null}

                            {!esMoral ? (
                                <>
                                    <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3 mt-4">
                                        E. Beneficiario controlador
                                    </h6>
                                    <FormGroup>
                                        <Label className="small font-weight-bold text-muted">
                                            ¿Conoce usted a algún beneficiario controlador?
                                        </Label>
                                        <div className="d-flex align-items-center flex-wrap mt-1">
                                            <CustomInput
                                                type="radio"
                                                id="conoceBcSi"
                                                name="pld_conoce_beneficiario_controlador"
                                                label="Sí"
                                                checked={
                                                    formData.pld_conoce_beneficiario_controlador ===
                                                    'si'
                                                }
                                                onChange={() =>
                                                    setFormData({
                                                        ...formData,
                                                        pld_conoce_beneficiario_controlador: 'si',
                                                    })
                                                }
                                            />
                                            <CustomInput
                                                className="mx-3"
                                                type="radio"
                                                id="conoceBcNo"
                                                name="pld_conoce_beneficiario_controlador"
                                                label="No"
                                                checked={
                                                    formData.pld_conoce_beneficiario_controlador ===
                                                    'no'
                                                }
                                                onChange={() =>
                                                    setFormData({
                                                        ...formData,
                                                        pld_conoce_beneficiario_controlador: 'no',
                                                        pld_beneficiario_controlador_nombre: '',
                                                    })
                                                }
                                            />
                                        </div>
                                    </FormGroup>
                                    {formData.pld_conoce_beneficiario_controlador === 'si' ? (
                                        <Row>
                                            <Col md="8">
                                                <FormGroup>
                                                    <Label className="small font-weight-bold text-muted text-uppercase">
                                                        ¿Quién? (nombre completo)
                                                    </Label>
                                                    <Input
                                                        value={
                                                            formData.pld_beneficiario_controlador_nombre
                                                        }
                                                        onChange={(e) =>
                                                            setFormData({
                                                                ...formData,
                                                                pld_beneficiario_controlador_nombre:
                                                                    e.target.value,
                                                            })
                                                        }
                                                        placeholder="Nombre completo del beneficiario controlador"
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    ) : null}
                                </>
                            ) : null}

                            <Button
                                color="primary"
                                size="lg"
                                block
                                className="mt-4 font-weight-bold"
                                type="button"
                                disabled={guardando || readOnly}
                                onClick={handleSave}
                            >
                                <FontAwesomeIcon
                                    icon={guardando ? faSpinner : faSave}
                                    spin={guardando}
                                    className="mr-2"
                                />
                                Guardar datos en expediente
                            </Button>
                        </fieldset>
                    ) : null}

                    {tab === 'subir' ? (
                        <div className="text-center py-5 border border-dashed rounded bg-white">
                            {readOnly ? (
                                <p className="text-muted mb-0">
                                    No tiene permiso para subir archivos.
                                </p>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faCloudUploadAlt}
                                        className="text-muted mb-3"
                                        size="3x"
                                    />
                                    <p className="text-muted font-weight-bold px-4">
                                        Si el cliente ya entregó el formato físico, súbalo aquí
                                        para integrarlo al expediente.
                                    </p>
                                    <Label
                                        className={`btn btn-dark mt-2 mb-0 ${subiendoArchivo ? 'disabled' : ''
                                            }`}
                                    >
                                        {subiendoArchivo ? (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faSpinner}
                                                    spin
                                                    className="mr-2"
                                                />
                                                Subiendo…
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faCloudUploadAlt}
                                                    className="mr-2"
                                                />
                                                Seleccionar archivo
                                            </>
                                        )}
                                        <Input
                                            type="file"
                                            className="d-none"
                                            accept=".pdf,image/*"
                                            disabled={subiendoArchivo}
                                            onChange={handleSubirFormato}
                                        />
                                    </Label>
                                </>
                            )}
                        </div>
                    ) : null}

                    {tab === 'xls' && !esMoral ? (
                        <div className="text-center py-5 border border-dashed rounded bg-white">
                            {readOnly ? (
                                <p className="text-muted mb-0">
                                    No tiene permiso para subir archivos.
                                </p>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faFileExcel}
                                        className="text-muted mb-3"
                                        size="3x"
                                    />
                                    <p className="text-muted font-weight-bold px-4 mb-2">
                                        Suba el Excel (.xlxs) de respuestas Handkey (persona física).
                                        Nombres, apellidos y RFC deben coincidir con el sujeto ({sujeto.nombre} {sujeto.paterno} {sujeto.materno} {sujeto.rfc})
                                    </p>
                                    <p className="text-muted small px-4 mb-3">
                                        Formato: decargar formato de respuestas de google forms.
                                    </p>
                                    <Label
                                        className={`btn btn-dark mt-2 mb-0 ${cargandoXls ? 'disabled' : ''}`}
                                    >
                                        {cargandoXls ? (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faSpinner}
                                                    spin
                                                    className="mr-2"
                                                />
                                                Cargando…
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faFileExcel}
                                                    className="mr-2"
                                                />
                                                Seleccionar Archivo
                                            </>
                                        )}
                                        <Input
                                            type="file"
                                            className="d-none"
                                            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                            disabled={cargandoXls}
                                            onChange={handleSubirXls}
                                        />
                                    </Label>
                                </>
                            )}
                        </div>
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" type="button" onClick={onClose}>
                        Cerrar
                    </Button>
                </ModalFooter>
            </Modal>

            <HomonimiaModal
                isOpen={homonimia.open}
                onClose={() =>
                    setHomonimia({ open: false, resumen: null, nombreCompleto: '' })
                }
                nombreCompleto={homonimia.nombreCompleto}
                resumen={homonimia.resumen}
            />
        </>
    );
}
